// Màn "Phòng Kinh doanh" (QĐ 0483; CEO chốt 17a, 28/10/2026): 7 thẻ — Tình hình
// kinh doanh (đứng đầu, mở sẵn — CEO 5a 31/10/2026), Data & chốt (CEO 9a 31/10/2026),
// Cơ cấu (chỉ xem, HRM làm chủ),
// Mục tiêu & món trọng điểm, Ghi công chờ duyệt, Chất lượng tháng, Bảng tổng tháng.
// Máy chủ tính và cắt phạm vi; màn chỉ vẽ.
import { useCallback, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { QueryErrorNote } from "@/components/QueryErrorNote";
import { useAuthV2 } from "@/lib/authv2/store";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/tabs";
import { useSalesOrg } from "./api";
import { thisMonthVN } from "./labels";
import { DataChotTab } from "./components/DataChotTab";
import { OverviewTab } from "./components/OverviewTab";
import { StructureTab } from "./components/StructureTab";
import { TargetsTab } from "./components/TargetsTab";
import { PendingCreditsTab } from "./components/PendingCreditsTab";
import { QualityTab } from "./components/QualityTab";
import { SummaryTab } from "./components/SummaryTab";

export const SALES_TEAM_TABS = [
  { id: "overview", label: "Tình hình kinh doanh" },
  { id: "data-chot", label: "Data & chốt" },
  { id: "structure", label: "Cơ cấu" },
  { id: "targets", label: "Mục tiêu & món trọng điểm" },
  { id: "pending", label: "Ghi công chờ duyệt" },
  { id: "quality", label: "Chất lượng tháng" },
  { id: "summary", label: "Bảng tổng tháng" },
] as const;

export type SalesTeamTab = (typeof SALES_TEAM_TABS)[number]["id"];
export type SalesTeamSearch = { tab?: SalesTeamTab | undefined; month?: string | undefined };

export function isSalesTeamTab(v: unknown): v is SalesTeamTab {
  return typeof v === "string" && SALES_TEAM_TABS.some((t) => t.id === v);
}

type Props = {
  /** Trạng thái trên đường dẫn (`?tab=&month=`) — để quay lại và gửi link được. */
  search?: SalesTeamSearch;
  onNavigate?: (next: SalesTeamSearch) => void;
};

export function SalesTeamView({ search, onNavigate }: Props) {
  const staff = useAuthV2((s) => s.currentStaff);
  const [local, setLocal] = useState<SalesTeamSearch>(search ?? {});
  const s = onNavigate ? (search ?? {}) : local;
  const go = useCallback(
    (next: SalesTeamSearch) => (onNavigate ? onNavigate(next) : setLocal(next)),
    [onNavigate],
  );
  const month = s.month || thisMonthVN();
  const tab: SalesTeamTab = isSalesTeamTab(s.tab) ? s.tab : "overview";
  const org = useSalesOrg(!!staff);

  if (!staff) return <EmptyState title="Bạn chưa đăng nhập" />;

  return (
    <div className="px-[var(--space-page-x)] py-[var(--space-page-y)]">
      <PageHeader
        title="Phòng Kinh doanh"
        description="Tình hình kinh doanh của đội, cơ cấu tiểu đội theo HRM, mục tiêu tháng, ghi công doanh số chờ duyệt, chất lượng tháng và bảng tổng."
        guideTab={tab}
        actions={
          // Thẻ «Data & chốt» có dải chọn kỳ riêng, ô tháng không tác dụng ở đó.
          tab === "data-chot" ? undefined : (
            <label className="flex items-center gap-2 text-sm">
              <span className="text-muted">Tháng</span>
              <input
                type="month"
                aria-label="Tháng đang xem"
                value={month}
                onChange={(e) => e.target.value && go({ ...s, month: e.target.value })}
                className="min-h-11 min-w-0 rounded-md border border-border bg-white px-2"
              />
            </label>
          )
        }
      />
      {org.isError ? (
        <QueryErrorNote error={org.error} fallback="Không tải được cơ cấu Phòng Kinh doanh" />
      ) : !org.data ? (
        <p className="text-sm text-muted">Đang tải…</p>
      ) : (
        <Tabs value={tab} onValueChange={(v) => go({ ...s, tab: v as SalesTeamTab })}>
          <TabsList>
            {SALES_TEAM_TABS.map((t) => (
              <TabsTrigger key={t.id} value={t.id}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <OverviewTab key={month} month={month} org={org.data} />
          </TabsContent>
          <TabsContent value="data-chot" className="mt-4">
            <DataChotTab />
          </TabsContent>
          <TabsContent value="structure" className="mt-4">
            <StructureTab org={org.data} />
          </TabsContent>
          <TabsContent value="targets" className="mt-4">
            <TargetsTab key={month} org={org.data} month={month} />
          </TabsContent>
          <TabsContent value="pending" className="mt-4">
            <PendingCreditsTab month={month} />
          </TabsContent>
          <TabsContent value="quality" className="mt-4">
            <QualityTab key={month} org={org.data} month={month} />
          </TabsContent>
          <TabsContent value="summary" className="mt-4">
            <SummaryTab month={month} org={org.data} onGoTab={(t) => go({ ...s, tab: t })} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
